<script setup lang="ts">
import Header from '@/components/Common/Header.vue'
import MobileHeader from '@/components/Common/MobileHeader.vue'
import { watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import i18n from './i18n'
import { useLangStore } from '@/pinia/stores/lang'

const langStore = useLangStore()
const route = useRoute()

const localeByParam = {
  zh: 'zh-CN',
  en: 'en-US'
} as const

const paramByLocale = {
  'zh-CN': 'zh',
  'en-US': 'en'
} as const

const storageKey = 'site_lang'
const siteOrigin = (import.meta.env.VITE_SITE_ORIGIN as string | undefined)?.replace(/\/$/, '')

const toSupportedLocale = (lang: string | null | undefined) =>
  typeof lang === 'string' && lang.toLowerCase().startsWith('zh') ? 'zh-CN' : 'en-US'

const getInitialLocale = () => {
  const queryLang = new URLSearchParams(window.location.search).get('lang')
  if (queryLang === 'zh' || queryLang === 'en') {
    return localeByParam[queryLang]
  }

  const savedLang = window.localStorage.getItem(storageKey)
  if (savedLang) {
    return toSupportedLocale(savedLang)
  }

  return toSupportedLocale(window.navigator.language)
}

const upsertHeadLink = (selector: string, rel: string, href: string, hreflang?: string) => {
  let link = document.head.querySelector(selector) as HTMLLinkElement | null
  if (!link) {
    link = document.createElement('link')
    link.rel = rel
    document.head.appendChild(link)
  }
  link.href = href
  if (hreflang) {
    link.hreflang = hreflang
  } else {
    link.removeAttribute('hreflang')
  }
}

const syncLanguageMeta = (locale: 'zh-CN' | 'en-US') => {
  const currentUrl = new URL(window.location.href)
  currentUrl.hash = ''
  const zhUrl = new URL(currentUrl.toString())
  zhUrl.searchParams.set('lang', 'zh')
  const enUrl = new URL(currentUrl.toString())
  enUrl.searchParams.set('lang', 'en')
  const canonicalUrl = locale === 'zh-CN' ? zhUrl : enUrl
  const toHref = (url: URL) => {
    const pathWithQuery = `${url.pathname}${url.search}`
    return siteOrigin ? `${siteOrigin}${pathWithQuery}` : pathWithQuery
  }

  upsertHeadLink('link[rel="canonical"]', 'canonical', toHref(canonicalUrl))
  upsertHeadLink('link[rel="alternate"][hreflang="zh-CN"]', 'alternate', toHref(zhUrl), 'zh-CN')
  upsertHeadLink('link[rel="alternate"][hreflang="en-US"]', 'alternate', toHref(enUrl), 'en-US')
  upsertHeadLink('link[rel="alternate"][hreflang="x-default"]', 'alternate', toHref(enUrl), 'x-default')
}

const syncLanguage = (lang: string | null | undefined) => {
  const locale = toSupportedLocale(lang)
  if (langStore.currentLang !== locale) {
    langStore.setLang(locale)
  }
  if (i18n.global.locale.value !== locale) {
    i18n.global.locale.value = locale
  }

  const langParam = paramByLocale[locale]
  const nextUrl = new URL(window.location.href)
  if (nextUrl.searchParams.get('lang') !== langParam) {
    nextUrl.searchParams.set('lang', langParam)
    window.history.replaceState({}, '', nextUrl.toString())
  }

  document.documentElement.lang = locale
  window.localStorage.setItem(storageKey, locale)
  syncLanguageMeta(locale)
}

onMounted(() => {
  syncLanguage(getInitialLocale())
})

watch(
  () => route.fullPath,
  () => {
    syncLanguage(langStore.currentLang)
  }
)

watch(
  () => langStore.currentLang,
  (newLang: string) => {
    syncLanguage(newLang)
  },
  { immediate: true }
)

watch(
  () => i18n.global.locale.value,
  (newLang) => {
    syncLanguage(newLang)
  }
)
</script>

<template>
  <div class="app">
    <!-- 桌面端Header -->
    <Header class="desktop-only" />
    <!-- 移动端Header -->
    <MobileHeader class="mobile-only" />
    <router-view></router-view>
  </div>
</template>

<style lang="scss">
.app {
  min-height: 100vh;
  background: url('/images/背景1.jpg') no-repeat center center fixed;
  background-size: cover;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0.7) 0%, rgba(189, 215, 238, 0.6) 100%);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    z-index: 0;
    backdrop-filter: blur(12px) saturate(180%);
  }

  > * {
    position: relative;
    z-index: 2;
  }
  
} 

// 显示控制类
.desktop-only {
  display: block;
}

.mobile-only {
  display: none;
}

/* 移动端适配 */
@media screen and (max-width: 768px) {
  .desktop-only {
    display: none !important;
  }

  .mobile-only {
    display: block !important;
  }
}

// 重置默认样式
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen,
    Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  line-height: 1.5;
}

a {
  text-decoration: none;
  color: inherit;
}

img {
  max-width: 100%;
  height: auto;
}
</style>
